import React, { useContext, useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import Search from "./Search";
import Filters from "./Filters";
import LayerDropdown from "./LayerDropdown";
import { MAP_LAYERS } from "../../config/mapLayers";
import { Logo } from "@healthdesk/shared-components";
import nm from "../../assets/images/branding/nm.jpg";
import wray from "../../assets/images/branding/wray.jpg";
import MainMenu from "../common/layout/MainMenu";
import ColorGradient from "../../components/dashboard/ColorGradient";
import { useNavigate } from "react-router-dom";
import { useMap } from "react-leaflet";

import {
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from "@mui/icons-material";
import { AuthContext } from "@healthdesk/shared-hooks";
import { tokens } from "@healthdesk/shared-utils";
import FeaturedAd from "./FeaturedAd";
import AdCarousel from "./AdCarousel";
import {
  MAP_HEADER_FLOAT_ROW_TOP_PX,
  MAP_HEADER_ZOOM_STACK_TOP_PX,
} from "./mapHeaderFloatLayout";

const _LogoWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const _Popup = styled.p`
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  font-weight: 400 !important;

  a {
    color: ${(props) => props.theme.colors.secondary};
  }
`;

const _MarketingLogoWrapper = styled.div`
  text-align: center;
  padding: 0.25rem 0.5rem 0.5rem;
`;

const _MarketingLogo = styled.img`
  margin: auto;
  max-height: 40px;
`;

const _WaitRoomTimeWrapper = styled.div`
  /* position: absolute; */
  top: 100%;
  background-color: ${(props) => props.theme.colors.white};
  left: 0;
  right: 0;
  margin: 0 auto;
  max-width: 720px;
  padding: 0.125em 0.275em;
`;

const SearchButton = ({ onClick }) => (
  <IconButton
    onClick={onClick}
    sx={{
      position: "absolute",
      top: `calc(100% + ${MAP_HEADER_FLOAT_ROW_TOP_PX}px)`,
      right: 5,
      zIndex: 10002,
      backgroundColor: "background.paper",
      boxShadow: 2,
      width: 36,
      height: 36,
      "&:hover": {
        backgroundColor: "background.paper",
        boxShadow: 4,
      },
    }}
  >
    <SearchIcon color="primary" />
  </IconButton>
);

const ZoomButton = () => {
  const map = useMap();

  return (
    <Box
      sx={{
        position: "absolute",
        top: `calc(100% + ${MAP_HEADER_ZOOM_STACK_TOP_PX}px)`,
        right: 5,
        zIndex: 10002,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <IconButton
        onClick={() => map.zoomIn()}
        size="small"
        sx={{
          backgroundColor: "background.paper",
          boxShadow: 2,
          width: 32,
          height: 32,
          borderRadius: "4px 4px 0 0",
          "&:hover": {
            backgroundColor: "background.paper",
            boxShadow: 4,
          },
        }}
      >
        <AddIcon color="primary" fontSize="small" />
      </IconButton>
      <IconButton
        onClick={() => map.zoomOut()}
        size="small"
        sx={{
          backgroundColor: "background.paper",
          boxShadow: 2,
          width: 32,
          height: 32,
          borderRadius: "0 0 4px 4px",
          marginTop: "1px",
          "&:hover": {
            backgroundColor: "background.paper",
            boxShadow: 4,
          },
        }}
      >
        <RemoveIcon color="primary" fontSize="small" />
      </IconButton>
    </Box>
  );
};

const HeaderWrapper = styled.div`
  position: ${(p) => (p.$stacked ? "relative" : "absolute")};
  height: auto;
  padding: 5px 5px 0 5px;
  font-size: 20px;
  ${(p) =>
    p.$stacked
      ? ""
      : `
  left: 0;
  right: 0;
  top: 0;
  `}
  width: 100%;
  box-sizing: border-box;
  background: ${tokens.colors.bgCard.light};
  font-size: 12px;
  z-index: 9999;
  box-shadow: 0px 2px 4px -1px rgba(0, 0, 0, 0.2),
    0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12);

  p {
    text-align: center;
    font-weight: 700;
    color: ${(props) => props.theme.colors.primary};
    margin-top: 8px;
    margin-bottom: 8px;
    font-size: 12px;
    z-index: 99;
  }
  .center {
    text-align: center;
  }
  p {
    text-align: center;
    font-weight: 700;
    color: ${(props) => props.theme.colors.primary};
    margin-top: 8px;
    margin-bottom: 8px;
    font-size: 12px;
  }
  h2 {
    text-align: center;
    font-weight: 700;
    color: ${(props) => props.theme.colors.primary};
    margin-top: 2px;
    margin-bottom: 6px;
    font-size: 16px;
  }
`;

// Custom styled component to replace the Columns component for the header
const HeaderRow = styled.div`
  display: flex;
  position: relative;
  align-items: center;
  width: 100%;
  padding: 5px 0;
  min-height: 42px;
`;

// Style for the left side containing logo and menu
const LeftSide = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  left: 0;
`;

// Style for the right side containing login button
const RightSide = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  right: 0;
`;

const Header = ({
  data,
  filter,
  setFilter,
  setCoords,
  updateMap,
  searchLoaded,
  currentRegion,
  setRegional,
  setCustomLocation,
  vaccine,
  ads,
  branding,
  onLocationClick,
  onAuthClick,
  onOpenBeamModal,
  expanded = true,
  full = true,
  menuInBar = false,
  showAds = true,
  adListings,
  listingsLoaded: adListingsLoaded,
  /** When true, header is in normal flow above the map (CareMap /); carousel may render outside Header. */
  stacked = false,
  /** When false, hide government/restricted-access filter checkbox (SCMap uses EmptyMapHeader instead). */
  showRestrictedAccessToggle = true,
  /** When false, {@link AdCarousel} is omitted (e.g. rendered below header in Map stacked layout). */
  adCarouselInHeader = true,
  /** When true, zoom +/- is rendered by Map (stacked layout only). */
  hideMapZoomControls = false,
  showLayerOverlay = true,
  selectedLayer = "",
  setSelectedLayer = () => {},
  layerLoading = false,
  layerMeta = null,
  layerError = null,
  overlayMenuLayers = MAP_LAYERS,
}) => {
  const [showSearch, setShowSearch] = useState(false);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleSortLevelOfCare = () => {

    if (user === null) {
      navigate("/chat", {
        state: {
          initiateChatWith: "Assess what level of care I need right now",
          assistantId: "general",
        },
      });
    } else {
      navigate("/dashboard/chat", {
        state: {
          initiateChatWith: "Assess what level of care I need right now",
          assistantId: "general",
        },
      });
    }
  };

  if (!full && !expanded) return;

  return (
    <HeaderWrapper $stacked={stacked}>
      {full && (
        <HeaderRow>
          {!menuInBar && (
            <LeftSide>
              <MainMenu />
              <Logo />
            </LeftSide>
          )}

          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              pointerEvents: "none",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: "18px",
                fontWeight: "700",
                color: "text.secondary",
                margin: 0,
                textAlign: "center",
              }}
            >
              {filter.group
                ? `${filter.group.replace(/-/g, " ")} Facilities`
                : "Priority Healthcare Availability"}
            </Typography>
          </Box>

          <RightSide>
            {onOpenBeamModal && (
              <Button
                variant="outlined"
                size="small"
                onClick={onOpenBeamModal}
                sx={{
                  borderColor: "primary.main",
                  color: "primary.main",
                  mr: 1,
                  "&:hover": {
                    borderColor: tokens.colors.mapHeaderOutlinedHoverBorder.light,
                    backgroundColor: "rgba(27, 69, 132, 0.08)",
                  },
                }}
              >
                Explore
              </Button>
            )}
            {user ? (
              <Link to="/dashboard">
                <Button variant="contained" color="primary" size="small">
                  Dashboard
                </Button>
              </Link>
            ) : (
              <Button
                variant="contained"
                color="secondary"
                size="small"
                onClick={onAuthClick}
              >
                Subscribe/Log In
              </Button>
            )}
          </RightSide>
        </HeaderRow>
      )}

      {/* Group filter indicator with action button */}
      {filter.group && (
        <Alert
          severity="info"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setFilter((prev) => ({ ...prev, group: "" }))}
              sx={{ fontSize: "0.75rem" }}
            >
              Show All Facilities
            </Button>
          }
          sx={{
            mt: 1,
            py: 0.5,
            fontSize: "0.875rem",
          }}
        >
          You're viewing only{" "}
          <strong>
            {filter.group
              .replace(/-/g, " ")
              .replace(/\b\w/g, (l) => l.toUpperCase())}
          </strong>{" "}
          facilities.
        </Alert>
      )}
      {showLayerOverlay && (
        <LayerDropdown
          layers={overlayMenuLayers}
          selectedLayer={selectedLayer}
          onLayerChange={setSelectedLayer}
          layerLoading={layerLoading}
          layerMeta={layerMeta}
          layerError={layerError}
        />
      )}

      {/* These are temporary logos hardcoded for regional demos--when regional feature is built out these will be removed */}
      {currentRegion === "nm" && (
        <div className="center">
          <Logo src={nm} width="175px" />
        </div>
      )}
      {currentRegion === "wray" && (
        <div className="center">
          <img src={wray} width="175px" alt="Wray logo" />
        </div>
      )}
      {currentRegion === "nm" && (
        <div className="center">
          <label>
            <input
              type="checkbox"
              onChange={(e) => setRegional(e.target.checked)}
            />
            Include other medical groups
          </label>
        </div>
      )}

      {currentRegion !== "wray" && (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            padding: 0,
          }}
        >
          {/* Left box */}
          <Box
            sx={{
              width: { xs: "100%", md: "50%" },
              mb: { xs: 0, md: 0 },
              padding: "0 10px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
              }}
            >
              <Filters
                data={data}
                filter={filter}
                setFilter={setFilter}
                onSortLevelOfCare={handleSortLevelOfCare}
                vaccine={vaccine}
                ads={ads}
                showRestrictedAccessToggle={showRestrictedAccessToggle}
                sx={{ margin: "0 !important", width: "100%" }}
              />
            </Box>
          </Box>

          {/* Right box - Your HealthDesk Score / ColorGradient */}
          <Box
            sx={{
              width: { xs: "100%", md: "50%" },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 10px",
            }}
          >
            <ColorGradient
              sx={{ width: "100%" }}
              facilityType={filter.facility}
            />
          </Box>
        </Box>
      )}

      {/* Featured Ad - Premium placement */}
      {ads?.length > 0 && ads.find((ad) => ad.featured) && (
        <Box sx={{ px: 1, pb: 1 }}>
          <FeaturedAd
            ad={ads.find((ad) => ad.featured)}
            onLocationClick={onLocationClick}
            setFilter={setFilter}
          />
        </Box>
      )}

      {/* Fallback: If no featured ad is found, use the first ad */}
      {ads?.length > 0 &&
        !ads.find((ad) => ad.featured) &&
        ads[0]?.website &&
        ads[0]?.logo && (
          <Box sx={{ px: 1, pb: 1 }}>
            <FeaturedAd
              ad={ads[0]}
              onLocationClick={onLocationClick}
              setFilter={setFilter}
            />
          </Box>
        )}

      {showAds && adCarouselInHeader && (
        <AdCarousel
          ads={adListings}
          listingsLoaded={adListingsLoaded}
          onLocationClick={onLocationClick}
          setFilter={setFilter}
        />
      )}

      <SearchButton onClick={() => setShowSearch(!showSearch)} />
      {!hideMapZoomControls && <ZoomButton />}
      <Search
        searchLoaded={searchLoaded}
        open={showSearch}
        setCoords={setCoords}
        setCustomLocation={setCustomLocation}
      />
    </HeaderWrapper>
  );
};

export default Header;
